// glsurface.ts — GlDibBitmap: the GL-backed DibBitmap surface.
//
// Mirrors game/bitmap.cpp's SDL arm where every DibBitmap is a GPU target
// texture, while reproducing CpuDibBitmap's pixel math byte-for-byte (the
// fragment shader in glrenderer.ts). Extends CpuDibBitmap with a ZERO-LENGTH
// rgba so the whole forms/render stack (typed against CpuDibBitmap) flows
// through unchanged — Suballoc'd SubBitmaps delegate their ops back here, and
// any illegal direct rgba access trips on the empty array instead of silently
// rendering wrong.
//
// Geometry contracts (must mirror dibbitmap.ts exactly):
//   blitCore: dst rect = src rect size at (xDst,yDst); per-pixel skip outside
//             EITHER surface — here: geometry clipped to the target, source
//             bounds checked in-shader (sp = srcOffset + dstPixel).
//   Fill/Shadow clamp to the surface; DrawLine is Bresenham with inclusive
//   endpoints; Scroll snapshots the whole surface then blends shifted.

import { CpuDibBitmap, SubBitmap, type PixelSource } from '../dibbitmap.ts';
import type { AtlasImage, Rect, Color } from '../texatlasmgr.ts';
import {
  GlRenderer,
  type SurfaceData,
  type ResolvedSrc,
  KIND_FILL,
  KIND_BLEND,
  KIND_TINTED,
  KIND_SHADOW,
  KIND_SCALED,
  CLASS_MIXED,
  CLASS_TRANS,
  packColor,
} from './glrenderer.ts';

const WHITE = 0xffffff;

export class GlDibBitmap extends CpuDibBitmap {
  readonly eng: GlRenderer;
  readonly data: SurfaceData;

  constructor(eng: GlRenderer, data: SurfaceData, cx: number, cy: number) {
    super(cx, cy, new Uint8Array(0));
    this.eng = eng;
    this.data = data;
  }

  /** the GL marker the render-time hooks (sprites.ts) dispatch on */
  readonly isGlSurface = true;

  dispose(): void {
    this.eng.destroySurface(this.data);
  }
  readback(): Uint8Array {
    return this.eng.readback(this.data);
  }

  // ---- source resolution -------------------------------------------------------

  /** Resolve any Blt source to a texture-resident image.
   *  null tex = THIS surface's snapshot (self-blit). */
  private resolveSrc(
    src: AtlasImage | PixelSource | CpuDibBitmap,
  ): ResolvedSrc & { self: boolean } {
    // SubBitmap views (the form-mgr strips) — walk to the root surface
    let vx = 0,
      vy = 0,
      cur: AtlasImage | PixelSource | CpuDibBitmap = src;
    let viewCx = -1,
      viewCy = -1;
    while (cur instanceof SubBitmap) {
      const v = cur.viewOf();
      vx += v.x;
      vy += v.y;
      if (viewCx < 0) {
        viewCx = cur.cx;
        viewCy = cur.cy;
      }
      cur = v.parent;
    }
    if (cur instanceof GlDibBitmap) {
      const scx = viewCx >= 0 ? viewCx : cur.data.cx;
      const scy = viewCy >= 0 ? viewCy : cur.data.cy;
      if (cur === this) {
        // self-source: sample the snapshot (texture == render target otherwise)
        return {
          tex: null,
          ox: vx,
          oy: vy,
          scx,
          scy,
          cls: CLASS_MIXED,
          classMap: null,
          self: true,
        };
      }
      return {
        tex: cur.data.tex,
        ox: vx,
        oy: vy,
        scx,
        scy,
        cls: CLASS_MIXED,
        classMap: null,
        self: false,
      };
    }
    if (cur instanceof CpuDibBitmap) {
      // a real CPU dib used as a source (TBitmap::Flash scratch) — PixelSource
      if (cur.rgba.length === 0) throw new Error('GlDibBitmap: empty CPU dib as source');
      return { ...this.eng.resolveSource(cur), self: false };
    }
    // plain objects: AtlasImage carries `decal`; PixelSource is bare pixels
    if ((cur as AtlasImage).decal !== undefined) {
      return { ...this.eng.resolveAtlas(cur as AtlasImage), self: false };
    }
    return { ...this.eng.resolveSource(cur as PixelSource), self: false };
  }

  /** clip a dst rect to this surface (and an optional extra clip rect) */
  private clip(x: number, y: number, w: number, h: number, rcClip: Rect | null): Rect {
    let l = Math.max(0, x),
      t = Math.max(0, y);
    let r = Math.min(this.data.cx, x + w),
      b = Math.min(this.data.cy, y + h);
    if (rcClip !== null) {
      l = Math.max(l, rcClip.left);
      t = Math.max(t, rcClip.top);
      r = Math.min(r, rcClip.right);
      b = Math.min(b, rcClip.bottom);
    }
    return { left: l, top: t, right: r, bottom: b };
  }

  // ---- 1:1 blits (Blt / BltModulated / BltTinted) --------------------------------

  private blt1(
    src: AtlasImage | PixelSource | CpuDibBitmap,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    kind: number,
    colorPacked: number,
    opacity = 255,
  ): void {
    const g = this.resolveSrc(src);
    const sl = prcSrc !== null ? prcSrc.left : 0;
    const st = prcSrc !== null ? prcSrc.top : 0;
    const sw = prcSrc !== null ? prcSrc.right - prcSrc.left : g.scx;
    const sh = prcSrc !== null ? prcSrc.bottom - prcSrc.top : g.scy;
    if (sw <= 0 || sh <= 0) return;

    const geom = this.clip(xDst, yDst, sw, sh, null);
    if (geom.right <= geom.left || geom.bottom <= geom.top) return;

    // sp = srcOffset + dstPixel (texture coords); valid texel bounds = the
    // source image's extent within its texture, TIGHTENED to the geometry's
    // mapped rect (identical for normal quads — pixels outside geom never
    // rasterize — but load-bearing for FUSED quads, where the union geometry
    // extends past this blit and those pixels must read as alpha 0)
    const offX = g.ox + sl - xDst;
    const offY = g.oy + st - yDst;
    const b0 = Math.max(g.ox, offX + geom.left);
    const b1 = Math.max(g.oy, offY + geom.top);
    const b2 = Math.min(g.ox + g.scx, offX + geom.right);
    const b3 = Math.min(g.oy + g.scy, offY + geom.bottom);

    // alpha class of the sampled region decides the dst-read path
    let readsDst: boolean;
    if (kind === KIND_TINTED || g.self) {
      readsDst = true; // additive always reads dst; self-blits read the snapshot
    } else if (g.classMap !== null) {
      const cls = GlRenderer.rectClass(g, sl, st, sl + sw, st + sh);
      if (cls === CLASS_TRANS) return; // every pixel skips — nothing to draw
      readsDst = cls === CLASS_MIXED;
    } else if (g.cls === CLASS_TRANS) {
      return;
    } else {
      readsDst = g.cls === CLASS_MIXED;
    }

    // A faded blit (opacity<255) becomes a fractional-alpha blend even for opaque art, so it
    // MUST read the destination (else it blends over black, not the terrain). Pack the
    // opacity into the free top byte of the color word (shader scales s.a by it); 255 packs
    // nothing → byte-identical to today.
    if (opacity < 255) readsDst = true;
    const packed = opacity < 255 ? colorPacked | (opacity << 24) : colorPacked;
    const readRects: Rect[] = readsDst ? [geom] : [];
    if (g.self) {
      // the surface region this op samples (conservative: the full src rect)
      readRects.push(this.clip(g.ox + sl, g.oy + st, sw, sh, null));
    }
    this.eng.enqueue(
      this.data,
      geom,
      g.tex,
      offX,
      offY,
      0,
      0,
      b0,
      b1,
      b2,
      b3,
      kind,
      readsDst,
      packed,
      readRects,
    );
  }

  // dibbitmap.ts:66-79 — BLENDMODE_BLEND copy
  override Blt(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    opacity = 255,
  ): void {
    this.blt1(src, prcSrc, xDst, yDst, KIND_BLEND, WHITE, opacity);
  }

  // dibbitmap.ts:82-95 — ColorMod + ADD (team-color decals)
  override BltTinted(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    clr: Color,
    opacity = 255,
  ): void {
    this.blt1(src, prcSrc, xDst, yDst, KIND_TINTED, packColor(clr), opacity);
  }

  // dibbitmap.ts:98-110 — ColorMod + BLEND (glyph recolor)
  override BltModulated(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    clr: Color,
  ): void {
    this.blt1(src, prcSrc, xDst, yDst, KIND_BLEND, packColor(clr));
  }

  // ---- solid ops -----------------------------------------------------------------

  // dibbitmap.ts:113-123 — opaque rect, alpha 255
  override Fill(x: number, y: number, cx: number, cy: number, clr: Color): void {
    const geom = this.clip(x, y, cx, cy, null);
    this.eng.enqueue(
      this.data,
      geom,
      null,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      KIND_FILL,
      false,
      packColor(clr),
      NO_RECTS,
    );
  }

  // dibbitmap.ts:129-142 — black at AlphaMod 100, BLEND
  override Shadow(x: number, y: number, cx: number, cy: number): void {
    const geom = this.clip(x, y, cx, cy, null);
    if (geom.right <= geom.left || geom.bottom <= geom.top) return;
    this.eng.enqueue(this.data, geom, null, 0, 0, 0, 0, 0, 0, 0, 0, KIND_SHADOW, true, 0, [geom]);
  }

  // dibbitmap.ts:146-162 — Bresenham, inclusive endpoints, opaque pixels
  override DrawLine(x1: number, y1: number, x2: number, y2: number, clr: Color): void {
    this.linePixels(x1, y1, x2, y2, clr, null);
  }

  /** sprites.ts drawLineClipped's GL arm (per-pixel clip-rect test) */
  glDrawLineClipped(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    clr: Color,
    rcClip: Rect,
  ): void {
    this.linePixels(x1, y1, x2, y2, clr, rcClip);
  }

  private linePixels(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    clr: Color,
    rcClip: Rect | null,
  ): void {
    const packed = packColor(clr);
    if (x1 === x2 || y1 === y2) {
      // axis-aligned: the Bresenham walk is a contiguous inclusive span
      const l = Math.min(x1, x2),
        t = Math.min(y1, y2);
      const geom = this.clip(l, t, Math.abs(x2 - x1) + 1, Math.abs(y2 - y1) + 1, rcClip);
      this.eng.enqueue(
        this.data,
        geom,
        null,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        KIND_FILL,
        false,
        packed,
        NO_RECTS,
      );
      return;
    }
    const dx = Math.abs(x2 - x1),
      dy = Math.abs(y2 - y1);
    const sx = x1 < x2 ? 1 : -1,
      sy = y1 < y2 ? 1 : -1;
    let err = dx - dy;
    let x = x1,
      y = y1;
    for (;;) {
      if (
        x >= 0 &&
        x < this.data.cx &&
        y >= 0 &&
        y < this.data.cy &&
        (rcClip === null ||
          (x >= rcClip.left && x < rcClip.right && y >= rcClip.top && y < rcClip.bottom))
      ) {
        this.eng.enqueue(
          this.data,
          { left: x, top: y, right: x + 1, bottom: y + 1 },
          null,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          KIND_FILL,
          false,
          packed,
          NO_RECTS,
        );
      }
      if (x === x2 && y === y2) break;
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
  }

  // ---- self ops --------------------------------------------------------------------

  // dibbitmap.ts:166-169 — snapshot the surface, then BLEND the shifted copy
  // (the C++ GPU pass reads pre-copy pixels; CpuDibBitmap snapshots — identical)
  override Scroll(prcSrc: Rect, xDst: number, yDst: number): void {
    const sw = prcSrc.right - prcSrc.left,
      sh = prcSrc.bottom - prcSrc.top;
    if (sw <= 0 || sh <= 0) return;
    this.eng.freshSnapshot(this.data);
    const geom = this.clip(xDst, yDst, sw, sh, null);
    this.eng.enqueue(
      this.data,
      geom,
      null,
      prcSrc.left - xDst,
      prcSrc.top - yDst,
      0,
      0,
      0,
      0,
      this.data.cx,
      this.data.cy,
      KIND_BLEND,
      true,
      WHITE,
      NO_RECTS,
    ); // snapshot just synced — nothing dirty
  }

  // ---- the render-time hooks (sprites.ts dispatches when present) -------------------

  /** sprites.ts AnimSprite.Draw's GL arm — the scaled, alpha-keyed, clipped copy */
  glScaledBlit(
    src: PixelSource,
    xDst: number,
    yDst: number,
    cxDst: number,
    cyDst: number,
    rcClip: Rect | null,
  ): void {
    if (cxDst <= 0 || cyDst <= 0 || src.cx <= 0 || src.cy <= 0) return;
    const g = this.eng.resolveSource(src);
    if (g.cls === CLASS_TRANS) return;
    const geom = this.clip(xDst, yDst, cxDst, cyDst, rcClip);
    this.eng.enqueue(
      this.data,
      geom,
      g.tex,
      src.cx,
      src.cy,
      cxDst,
      cyDst,
      xDst,
      yDst,
      g.ox,
      g.oy,
      KIND_SCALED,
      false,
      WHITE,
      NO_RECTS,
    );
  }
}

const NO_RECTS: readonly Rect[] = [];
